import { create } from 'zustand';

import { ONBOARDINGS, ONBOARDING_FLOW } from './constants';
import {
  OnboardingKeys,
  OnboardingStepConfig,
  OnboardingStoreState,
  Refs,
} from './types';
import { getDisplayStep } from './utils/getDisplayStep';

const DEFAULT_STEP_STATE = {
  step: null,
  subStep: 0,
  displayStep: null,
};

export const useOnboardingStore = create<OnboardingStoreState>((set, get) => ({
  initialisedForUser: null,

  step: null,

  subStep: 0,

  displayStep: null,

  onboardingKey: null,

  skipped: [],

  initialized: false,

  refs: {} as Refs,

  initHandler: async (data): Promise<void> => {
    const states = await Promise.all(
      ONBOARDING_FLOW.filter((key) => !get().skipped.includes(key)).map((key) =>
        ONBOARDINGS[key].init({
          step: data.step,
          onboardingKey: get().onboardingKey, // If the onboardingKey was set => start from that onboarding and skip all previous
        })
      )
    );

    // Here we are creating the state to start the onboarding. The first non-nullable one gets the priority
    // In other words we try to get the first non-empty onboarding and start the process from it
    // We should not overwrite 'skipped' property as we want to know all skipped onboarding in advance
    const newState = states.reduce(
      (acc, current) => ({
        ...current,
        ...acc,
        skipped: acc.skipped!.concat(current.skipped || []),
      }),
      { skipped: get().skipped, initialized: true }
    );
    newState.skipped = Array.from(new Set(newState.skipped));
    set(newState);

    const { onboardingKey, step, setNextOnboarding, initializeStep } = get();
    if (!onboardingKey) {
      return setNextOnboarding();
    } else if (step) {
      return initializeStep(step);
    }
  },

  init: async (data, forceReinitialization = false): Promise<void> => {
    const { initialized, initialisedForUser, initHandler } = get();

    // We don't need to initialize the store if it was already initialized for the same user
    // unless we force reinitialization
    if (
      data.userId &&
      data.userId === initialisedForUser &&
      initialized &&
      !forceReinitialization
    ) {
      return;
    }

    const promise = initHandler(data);
    set({ initialized: promise, initialisedForUser: data.userId });
    await promise;
    set({ initialized: true });
  },

  skipOnboarding: async (keys = []): Promise<void> => {
    const { skipped, initialized, setNextOnboarding } = get();

    // We may skip onboarding before it was initialized, so we should not use 'flow' here
    const skippingOnboardingKeys = keys.length ? keys : ONBOARDING_FLOW;
    const newSkipped = Array.from(
      new Set(skipped.concat(skippingOnboardingKeys))
    );

    set({
      ...DEFAULT_STEP_STATE,
      skipped: newSkipped,
    });

    // If the store has been initialized already - skip current onboarding
    if (initialized === true) {
      await setNextOnboarding();
    }

    Promise.all(skippingOnboardingKeys.map((key) => ONBOARDINGS[key].skip()));
  },

  setSkipped: (keys: OnboardingKeys[]): void => {
    set({ skipped: keys });
  },

  resetOnboarding: async ({ resetKey, step, gotoKey } = {}): Promise<void> => {
    const { initialized } = get();

    // We may try to reset onboarding before it was initialized
    // To avoid rewriting data we need to wait for initialization to finish and only then reset the onboarding
    const handler = async (): Promise<void> => {
      const { skipped } = get();
      if (resetKey && !ONBOARDING_FLOW.includes(resetKey)) {
        return;
      }
      const resetPromise = Promise.all(
        (resetKey ? [resetKey] : ONBOARDING_FLOW).map((key) =>
          ONBOARDINGS[key].reset(step)
        )
      );

      let newSkipped: OnboardingKeys[] = [];
      if (resetKey) {
        let newKeyIndex = null;
        newSkipped = skipped.filter((key) => key !== resetKey);

        if (gotoKey) {
          const resetKeyIndex = ONBOARDING_FLOW.indexOf(resetKey);
          // If we have resetKey and gotoKey, let's put the right onboardingKey to start that onboarding
          // If we are already initialized => put the previous onboardingKey and call setNextOnboarding
          // If we are not initialized => put the right key
          newKeyIndex = resetKeyIndex - (initialized ? 1 : 0);

          // Also with gotoKey property we need to skip all previous onboardings
          ONBOARDING_FLOW.forEach((key, i) => {
            if (i < resetKeyIndex && !newSkipped.includes(key)) {
              newSkipped.push(key);
              ONBOARDINGS[key].skip();
            }
          });
        }

        const newKey =
          (typeof newKeyIndex === 'number' && ONBOARDING_FLOW[newKeyIndex]) ||
          null;
        set({
          ...DEFAULT_STEP_STATE,
          skipped: newSkipped,
          onboardingKey: newKey,
        });
        await get().setNextOnboarding(step);
        return;
      }

      // If the resetKey is not provided => reinit all onboardings
      await resetPromise;
      set({
        initialized: false,
        onboardingKey: null,
        skipped: newSkipped,
      });
      await get().init({ step });
    };

    if (typeof initialized === 'boolean') {
      await handler();
    } else {
      await initialized.then(handler);
    }
  },

  registerStepRef: (ref, step): void => {
    set((state) => ({
      refs: { ...state.refs, [step]: ref as HTMLElement },
    }));
  },

  setNextOnboarding: async (step): Promise<void> => {
    const { onboardingKey, skipped, setStep, setNextOnboarding } = get();

    // Finish if achieved the last onboarding in flow
    if (
      onboardingKey &&
      onboardingKey === ONBOARDING_FLOW[ONBOARDING_FLOW.length - 1]
    ) {
      return;
    }

    const nextOnboardingKey: OnboardingKeys | undefined =
      ONBOARDING_FLOW[
        onboardingKey ? ONBOARDING_FLOW.indexOf(onboardingKey) + 1 : 0
      ];
    if (!nextOnboardingKey) {
      return;
    }

    set({
      ...DEFAULT_STEP_STATE,
      onboardingKey: nextOnboardingKey,
    });

    if (skipped.includes(nextOnboardingKey)) {
      await setNextOnboarding();
      return;
    }

    const onboardingData = ONBOARDINGS[nextOnboardingKey].data;
    const stepName = step?.name;
    const newStep =
      stepName && stepName in onboardingData
        ? step
        : Object.values(onboardingData).find(({ index }) => index === 1);
    if (newStep) {
      await setStep(newStep, 0, true);
      return;
    }
    await setNextOnboarding();
  },

  setNextStep: async (): Promise<void> => {
    const {
      step: currentStep,
      subStep,
      onboardingKey,
      setStep,
      setNextOnboarding,
    } = get();

    if (!onboardingKey) {
      return;
    }

    const stepsCount = currentStep?.steps?.length;

    if (typeof stepsCount !== 'number' || !currentStep) {
      await setNextOnboarding();
    } else if (subStep + 1 < stepsCount) {
      await setStep(currentStep, subStep + 1, true);
    } else {
      // goto next step
      const nextStep = Object.values(ONBOARDINGS[onboardingKey].data).find(
        ({ index }) => index === currentStep.index + 1
      );

      if (nextStep) {
        await setStep(nextStep, 0, true);
      } else {
        await setNextOnboarding();
      }
    }
  },

  setStep: async (step, subStep = 0, next = false): Promise<void> => {
    const { onboardingKey, setNextOnboarding } = get();
    if (!onboardingKey) {
      return;
    }

    const onboardingData = ONBOARDINGS[onboardingKey]?.data;
    const stepName = step?.name;
    if (!onboardingData || !stepName || !(stepName in onboardingData)) {
      return;
    }

    // We should not wait for update to finish every time to speed up the process
    const update = ONBOARDINGS[onboardingKey].update(step, subStep, next);

    if (step.isSkip) {
      return setNextOnboarding();
    }

    const displayStep = getDisplayStep(onboardingKey, step, subStep);
    set({
      step,
      subStep,
      displayStep,
    });

    if (!displayStep) {
      // However, we have to wait for update to finish to avoid collisions between updating the steps of the same onboarding
      await update;
      return get().setNextStep();
    }
  },

  // This method is used to initialize the step without updating the API state
  initializeStep: async (
    step: OnboardingStepConfig,
    subStep = 0
  ): Promise<void> => {
    const { onboardingKey, setNextOnboarding } = get();
    if (!onboardingKey) {
      return;
    }
    await Promise.resolve(); // Adding an await expression to fix the lint error

    const onboardingData = ONBOARDINGS[onboardingKey]?.data;
    if (!onboardingData?.[step.name as keyof typeof onboardingData]) {
      return;
    }

    if (step.isSkip) {
      return setNextOnboarding();
    }

    const displayStep = getDisplayStep(onboardingKey, step, subStep);
    set({
      step,
      subStep,
      displayStep,
    });

    if (!displayStep) {
      return get().setNextStep();
    }
  },

  setStepIfActive: async (step, subStep = 0): Promise<boolean> => {
    const {
      step: currentStep,
      subStep: currentSubStep,
      onboardingKey,
      setStep,
    } = get();

    if (!onboardingKey) {
      return false;
    }

    const onboardingData = ONBOARDINGS[onboardingKey].data;
    const stepName = step?.name;
    const selectedStep =
      stepName && stepName in onboardingData
        ? (onboardingData as Record<string, OnboardingStepConfig>)[stepName]
        : undefined;

    if (!selectedStep || !currentStep || currentStep.isSkip) {
      return false;
    }

    const isLastSubStep =
      currentSubStep === currentStep.steps.length - 1 ||
      !currentStep.steps.length;

    if (
      // next step and current subStep was last one
      (currentStep.index === selectedStep.index - 1 && isLastSubStep) ||
      // or next sub step of current step
      (currentStep.index === selectedStep.index &&
        currentSubStep === subStep - 1)
    ) {
      await setStep(step, subStep);
      return true;
    }

    return false;
  },
}));

type PrivateFields =
  | 'skipped'
  | 'initialized'
  | 'initHandler'
  | 'setNextOnboarding'
  | 'initialisedForUser'
  | 'initializeStep';

type StoreSelector<T> = {
  [K in keyof T]: (state: T) => T[K];
};

export const onboardingStoreSelector: StoreSelector<
  Omit<OnboardingStoreState, PrivateFields>
> = {
  setStep: (state) => state.setStep,
  setNextStep: (state) => state.setNextStep,
  setStepIfActive: (state) => state.setStepIfActive,
  skipOnboarding: (state) => state.skipOnboarding,
  resetOnboarding: (state) => state.resetOnboarding,
  setSkipped: (state) => state.setSkipped,
  step: (state) => state.step,
  subStep: (state) => state.subStep,
  displayStep: (state) => state.displayStep,
  onboardingKey: (state) => state.onboardingKey,
  refs: (state) => state.refs,
  init: (state) => state.init,
  registerStepRef: (state) => state.registerStepRef,
};
